import { useState } from 'react';
import { PARTNER_STATUS_LABEL, PARTNER_TYPE_LABEL, type PartnerStatus } from '@dru/types';
import { Hospital, Pencil, Trash2 } from 'lucide-react';
import type { CursorPage, Partner } from '@dru/types';
import {
  Alert, Button, ConfirmDialog, DataTable, EntityCombobox, IconAction, InfoTip, RowActions,
  StatusBadge, TextField, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';

interface PreferredRow {
  id: string;
  note: string | null;
  rank: number;
  partner: Partner & { status?: string };
}

/**
 * The venues this company usually sends its people to.
 *
 * A preference and nothing stronger, and the screen says so: booking still
 * offers the whole empanelled network, because the day the preferred hospital
 * is full somebody still has to be screened. What the list buys is that an
 * executive arranging an examination sees their own four venues at the top
 * instead of picking from the national network by memory.
 *
 * The note is the half that makes the list maintainable. "Closest to the Vapi
 * plant" is a reason somebody can disagree with; a bare name is a decision
 * nobody dares change.
 */
export function PreferredVenues() {
  const { toast } = useToast();
  const { permissions } = useAuth();
  // Reading the list needs partner.read; changing it is a company setting.
  const canEdit = holds(permissions, 'settings.update');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PreferredRow | null>(null);
  const [removing, setRemoving] = useState<PreferredRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = useQuery<{ items: PreferredRow[] }>(() => api.get('/preferred-partners'), []);

  const search = async (q: string, cursor: string | null) => {
    const page = await api.get<CursorPage<Partner>>(
      `/partners?limit=20&status=ACTIVE&q=${encodeURIComponent(q)}${cursor ? `&cursor=${cursor}` : ''}`,
    );
    return { items: page.items, nextCursor: page.nextCursor };
  };

  async function remove(row: PreferredRow) {
    setError(null);
    try {
      await api.del(`/preferred-partners/${row.id}`);
      setRemoving(null);
      rows.reload();
      toast('success', `${row.partner.name} is off your list. Booking there still works.`);
    } catch (e) { setError(errorMessage(e)); }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-1">
          <h2 className="text-h4 text-neutral-900">Preferred venues</h2>
          <InfoTip label="About preferred venues">
            The hospitals and laboratories you usually use. They appear first when somebody
            arranges an examination. This closes nothing off — every empanelled venue stays
            bookable, which matters on the day your first choice is full.
          </InfoTip>
        </div>
        {canEdit && <Button size="sm" onClick={() => setAdding(true)}>Add a venue</Button>}
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <DataTable<PreferredRow>
        rows={rows.data?.items ?? []}
        loading={rows.loading}
        error={rows.error}
        onRetry={rows.reload}
        rowKey={(r) => r.id}
        empty={{
          title: 'No preferred venues yet',
          hint: 'Add the hospitals and laboratories near your plants. Everyone arranging an examination sees them first.',
          action: canEdit ? <Button onClick={() => setAdding(true)}>Add a venue</Button> : undefined,
        }}
        columns={[
          {
            key: 'venue', header: 'Venue', alwaysVisible: true,
            cell: (r) => (
              <div>
                <div className="flex items-center gap-1.5">
                  <Hospital className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                  <span className="font-medium text-neutral-900">{r.partner.name}</span>
                </div>
                <div className="font-mono text-caption text-neutral-500">
                  {[r.partner.code, r.partner.city].filter(Boolean).join(' · ')}
                </div>
              </div>
            ),
          },
          {
            key: 'kind', header: 'Kind',
            cell: (r) => (
              <span className="text-small text-neutral-700">
                {r.partner.networkType?.name ?? (r.partner.type ? PARTNER_TYPE_LABEL[r.partner.type] ?? r.partner.type : '—')}
              </span>
            ),
          },
          {
            key: 'why', header: 'Why this one',
            cell: (r) => (
              <span className="text-small text-neutral-700">
                {r.note ?? <span className="text-neutral-500">No reason recorded</span>}
              </span>
            ),
          },
          {
            key: 'offers', header: 'Tests offered', align: 'right',
            cell: (r) => (
              <span className="text-small tabular-nums text-neutral-700">{r.partner.testsOffered}</span>
            ),
          },
          {
            key: 'state', header: 'State',
            cell: (r) => (
              <StatusBadge tone={r.partner.status === 'ACTIVE' ? 'covered' : 'overdue'}>
                {PARTNER_STATUS_LABEL[r.partner.status as PartnerStatus] ?? r.partner.status ?? '—'}
              </StatusBadge>
            ),
          },
          ...(canEdit ? [{
            key: 'act', header: 'Actions', align: 'right' as const, alwaysVisible: true, mobileLabel: '',
            cell: (r: PreferredRow) => (
              <RowActions>
                <IconAction label={`Edit ${r.partner.name}`} icon={Pencil} onClick={() => setEditing(r)} />
                <IconAction label={`Remove ${r.partner.name}`} icon={Trash2} danger onClick={() => setRemoving(r)} />
              </RowActions>
            ),
          }] : []),
        ]}
      />

      {adding && (
        <AddDialog
          search={search}
          onClose={() => setAdding(false)}
          onDone={(name) => {
            setAdding(false); rows.reload();
            toast('success', `${name} added. It will appear first when somebody books.`);
          }}
        />
      )}

      {editing && (
        <EditDialog
          row={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); rows.reload(); toast('success', 'Saved.'); }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.partner.name}?`}
          what="It comes off your shortlist and stops appearing first in the picker."
          affects="Nothing else. You can still book there, and anything already booked is untouched."
          confirmLabel="Remove"
          onCancel={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
        />
      )}
    </div>
  );
}

function AddDialog({ search, onClose, onDone }: {
  search: (q: string, cursor: string | null) => Promise<{ items: Partner[]; nextCursor: string | null }>;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!partner) return;
    setBusy(true); setError(null);
    try {
      await api.post('/preferred-partners', {
        partnerId: partner.id, ...(note.trim() ? { note: note.trim() } : {}),
      }, crypto.randomUUID());
      onDone(partner.name);
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Add a preferred venue" info="Search the empanelled network. Adding one puts it at the top of the picker; it does not stop anybody booking elsewhere." onClose={onClose}>

      <div className="mt-4 space-y-3">
          <EntityCombobox<Partner>
            label="Venue"
            required
            value={partner}
            onChange={setPartner}
            fetchOptions={search}
            getKey={(p) => p.id}
            placeholder="Choose a venue…"
            searchPlaceholder="Hospital name, code or city"
            emptyMessage="No empanelled venue matches that."
            renderOption={(p) => (
              <>
                <span className="block font-medium text-neutral-900">{p.name}</span>
                <span className="block text-caption font-normal text-neutral-500">
                  {[p.code, p.city, (p.type ? PARTNER_TYPE_LABEL[p.type] ?? p.type : undefined), `${p.testsOffered} tests`]
                    .filter(Boolean).join(' · ')}
                </span>
              </>
            )}
          />

      <TextField
        label="Why this one (optional)"
        value={note}
        onChange={setNote}
        info="Worth writing. “Closest to the Vapi plant” is a reason somebody can disagree with later."
        placeholder="Closest to the Vapi plant"
      />
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!partner}>Add</Button>
      </div>
    </Dialog>
  );
}

function EditDialog({ row, onClose, onDone }: {
  row: PreferredRow; onClose: () => void; onDone: () => void;
}) {
  const [note, setNote] = useState(row.note ?? '');
  const [rank, setRank] = useState(String(row.rank));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.patch(`/preferred-partners/${row.id}`, {
        note: note.trim() || null,
        ...(rank.trim() ? { rank: Number(rank) } : {}),
      });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={row.partner.name} onClose={onClose}>
      <TextField
        label="Why this one" value={note} onChange={setNote} autoFocus
        placeholder="Closest to the Vapi plant"
      />
      <TextField
        className="mt-3 max-w-[10rem]"
        label="Order"
        value={rank}
        onChange={(v) => setRank(v.replace(/\D/g, ''))}
        inputMode="numeric"
        hint="Lower comes first. Ties fall back to the name."
      />

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy}>Save</Button>
      </div>
    </Dialog>
  );
}
